import React, { useRef } from "react";

import { Box, CircularProgress, Stack } from "@mui/material";

import { ButtonVariant } from "Constant/button";

import StandartButton from "../StandartButton";
import Styled from "./PageContainer.styled";

/**
 * @component
 * @description Generic page container WIP TODO: Should be available for every page
 * @param {object} props
 * @param {string} [props.title]
 * @param {string} [props.subtitle]
 * @param {boolean} [props.showBackButton]
 * @param {object} [props.additionalButtons]
 * @param {"detail"|"list"} [props.variant="detail"] - "detail" shows the back button and right-aligned actions; "list" stacks title/subtitle/actions like the Sources page
 * @param {boolean} [props.loading=false] - Shows a spinner overlay on content: solid white on first load, blurred semi-transparent on list refresh after content has rendered once
 * @param {boolean} [props.hasContent=false] - Set when the page already has (cached) data to show while `loading`; forces the blurred semi-transparent overlay instead of hiding the content behind solid white (stale-while-revalidate)
 * @param {Function} [props.onBack] - Custom back handler; when provided the back button calls this instead of navigating to the previous route
 * @param {object} props.children
 * @param {string} [props.containerClassName] - Class name for the container
 */

const PageContainer = ({
    title,
    subtitle,
    showBackButton,
    additionalButtons,
    variant = "detail",
    loading = false,
    hasContent = false,
    containerClassName,
    onBack,
    children,
}) => {
    const isList = variant === "list";
    const shouldShowBackButton =
        showBackButton !== undefined ? showBackButton : !isList;

    const hasRenderedContent = useRef(false);
    if (!loading) hasRenderedContent.current = true;

    const showSpinnerInsteadOfContent =
        loading && !hasContent && (!isList || !hasRenderedContent.current);

    return (
        <Styled.PageContainer>
            <Stack>
                {/** Header */}
                {isList
                    ? (title || subtitle || additionalButtons) && (
                          <Box mb={4}>
                              <Styled.Title>{title}</Styled.Title>
                              {subtitle && (
                                  <Styled.Subtitle>{subtitle}</Styled.Subtitle>
                              )}
                              {additionalButtons && (
                                  <Box mt={1}>{additionalButtons}</Box>
                              )}
                          </Box>
                      )
                    : (title || shouldShowBackButton) && (
                          <Stack
                              direction="row"
                              gap={2}
                              justifyContent="space-between"
                              flexWrap="wrap"
                              mb={4}
                          >
                              <Stack direction="row" gap={4}>
                                  {shouldShowBackButton && (
                                      <StandartButton
                                          {...(onBack
                                              ? { onClick: onBack }
                                              : { to: -1 })}
                                          variant={ButtonVariant.BLACK_OUTLINED}
                                          value={"Back"}
                                      />
                                  )}
                                  <Box display="flex" flexDirection="column">
                                      <Styled.Title>{title}</Styled.Title>
                                      {subtitle && (
                                          <Styled.Subtitle>
                                              {subtitle}
                                          </Styled.Subtitle>
                                      )}
                                  </Box>
                              </Stack>
                              <Box>{additionalButtons}</Box>
                          </Stack>
                      )}

                <Box position="relative" minHeight="60vh">
                    {loading && (
                        <Box
                            position="absolute"
                            display="flex"
                            justifyContent="center"
                            alignItems="flex-start"
                            sx={{
                                inset: 0,
                                ...(showSpinnerInsteadOfContent
                                    ? {
                                          backgroundColor: "#fff",
                                      }
                                    : {
                                          backdropFilter: "blur(4px)",
                                          backgroundColor:
                                              "rgba(255, 255, 255, 0.7)",
                                      }),
                                borderRadius: "inherit",
                                zIndex: 1000,
                            }}
                        >
                            <Box
                                sx={{
                                    mt: "30vh",
                                    transform: "translateY(-50%)",
                                    display: "flex",
                                }}
                            >
                                <CircularProgress color="primary" size={24} />
                            </Box>
                        </Box>
                    )}
                    <Box className={containerClassName}>{children}</Box>
                </Box>
            </Stack>
        </Styled.PageContainer>
    );
};

export default PageContainer;
